import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Close as RemoveIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { uploadMediaFileWeb } from '../../services/api/familypal/mediaUpload';
import { getMediaFiles } from '../../services/api/familypal/config';
import { getMediaFilesByFileIdDownloadUrl } from '../../services/api/familypal';

interface FileInfo {
  id: string;
  name: string;
}

interface FileAttachmentsProps {
  /** Current list of file IDs */
  fileIds: string[];
  /** Callback when file IDs change (updates local state) */
  onChange: (fileIds: string[]) => void;
  /** Callback to persist fileIds to the database immediately after upload/remove */
  onPersist?: (fileIds: string[]) => Promise<void>;
  /** The data record ID (required for uploads) */
  dataId?: string;
  /** Whether uploading is allowed (typically only when editing) */
  canUpload?: boolean;
  /** Section label */
  label?: string;
}

const FileAttachments: React.FC<FileAttachmentsProps> = ({
  fileIds,
  onChange,
  onPersist,
  dataId,
  canUpload = false,
  label = 'Attachments',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInfos, setFileInfos] = useState<FileInfo[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fetch file metadata when fileIds change
  useEffect(() => {
    let cancelled = false;
    if (fileIds.length === 0) {
      setFileInfos([]);
      return;
    }
    (async () => {
      try {
        const result = await getMediaFiles(fileIds);
        if (!cancelled) {
          setFileInfos(
            result.files.map((f: any) => ({
              id: f.id,
              name: f.name || f.key || f.id,
            }))
          );
        }
      } catch {
        // If metadata fetch fails, show IDs as fallback
        if (!cancelled) {
          setFileInfos(fileIds.map((id) => ({ id, name: id.slice(0, 8) + '...' })));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [fileIds]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !dataId) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const fileObj = {
        uri: URL.createObjectURL(file),
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
      };
      const uploaded = await uploadMediaFileWeb(fileObj, dataId, setUploadProgress);
      const updated = [...fileIds, uploaded.id];
      onChange(updated);
      if (onPersist) await onPersist(updated);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [dataId, fileIds, onChange]);

  const handleRemove = useCallback(async (fileId: string) => {
    const updated = fileIds.filter((id) => id !== fileId);
    onChange(updated);
    if (onPersist) await onPersist(updated);
  }, [fileIds, onChange, onPersist]);

  const handleDownload = useCallback(async (fileId: string) => {
    try {
      const response = await getMediaFilesByFileIdDownloadUrl({
        path: { file_id: fileId },
      } as any);
      const result = response.data as any;
      const url = result?.download_url || result?.downloadUrl || result?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // silent fail
    }
  }, []);

  if (fileIds.length === 0 && !canUpload) {
    return null;
  }

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {canUpload && dataId && (
            <Button
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Add File
            </Button>
          )}
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
            {error}
          </Typography>
        )}

        {uploading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}

        {fileInfos.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {fileInfos.map((f) => (
              <Box
                key={f.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.5,
                  pl: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <FileIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {f.name}
                </Typography>
                <IconButton size="small" onClick={() => handleDownload(f.id)} title="Download">
                  <DownloadIcon fontSize="small" />
                </IconButton>
                {canUpload && (
                  <IconButton size="small" onClick={() => handleRemove(f.id)} title="Remove">
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {fileIds.length === 0 && canUpload && (
          <Typography variant="body2" color="text.secondary">
            No files attached
          </Typography>
        )}

        {canUpload && !dataId && (
          <Typography variant="caption" color="text.secondary">
            Save first to attach files
          </Typography>
        )}

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
        />
      </Box>
    </>
  );
};

export default FileAttachments;
